import { AfterViewInit, Component, HostListener, Input, OnInit, OnDestroy, ViewChild, signal, effect, computed, untracked } from '@angular/core';
import { catchError, filter, firstValueFrom, of, pairwise, race, Subscription, take, throwError, timer, switchMap } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { ControlService } from '../library/control.service';
import { RealtimeService } from '../library/realtime/realtime.service';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { PlayerService } from '../library/player.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { IPathwayNextStepResponse, IPathwayRunStepResponse, IPathwayCompleteStepResponse, IPathwayExitStepResponse } from 'src/app/interfaces/ipathway-enrollment';
import { IPdfLearnState, IPdfProgress } from 'src/app/interfaces/ipdf-learn-state';
import { IVideoLearnState } from 'src/app/interfaces/ivideo-learn-state';
import { IScormLearnState } from 'src/app/interfaces/iscorm-learn-state';
import { PathwayStepState, PathwayStepType, isRoleplayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { AppConstants } from 'src/app/app-constants';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
  AudioCallControlService,
  ConversationState,
} from 'src/app/services/learner/audio-call-control.service';
import { CallPathwayVideoComponent } from './call-flow/call-pathway-video/call-pathway-video.component';
import { CallPathwayScormComponent } from './call-flow/call-pathway-scorm/call-pathway-scorm.component';
import { CallPathwayRoleplayComponent } from '../call-pathway-roleplay/call-pathway-roleplay.component';
import { PathwaySubtitleService, SubtitleContext } from './pathway-subtitle.service';
import { CallService } from 'src/app/services/learner/call.service';
import { IInitiatePathwayStepSimulationResponse, ICallScormChild } from 'src/app/interfaces/call/iinitiate-pathway-step-simulation';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';
import { downloadFileFromUrl } from 'src/app/utils/file-download.util';

const DEFAULT_PDF_URL =
  '/assets/pdf/NOV_IMUS_One-BA_Selling_Approach_Sales_Field_Guide_6.23_FINAL_2.pdf';

type DeviceSettingsModalContext = Readonly<{
  wasPausedBeforeOpen: boolean;
  micOnBeforeOpen: boolean;
  videoOnBeforeOpen: boolean;
}>;

@Component({
  host: {
    class: 'theme-dark',
  },
  selector: 'app-call-pathway',
  standalone: false,
  templateUrl: './call-pathway.component.html',
  styleUrls: ['./call-pathway.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'learningPathway',
    },
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'call',
    },
  ],
})
export class CallPathwayComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() controlService: ControlService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() callStore: CallStoreService | undefined;
  private readonly _enrollmentStepUuid = signal<string>('');
  private _enrollmentStepUuidReady = false;
  @Input() set enrollmentStepUuid(value: string) { this._enrollmentStepUuid.set(value); }
  get enrollmentStepUuid(): string { return this._enrollmentStepUuid(); }
  @Input() enrollmentUuid: string = '';
  private readonly _stepType = signal<string>(PathwayConstants.STEP_TYPE_INTRO);
  @Input() set stepType(value: string) { this._stepType.set(value); }
  get stepType(): string { return this._stepType(); }
  protected get isRoleplayStepType() { return isRoleplayStepType(this.stepType); }

  @ViewChild(CallPathwayVideoComponent) private videoComponent?: CallPathwayVideoComponent;
  @ViewChild(CallPathwayScormComponent) private scormComponent?: CallPathwayScormComponent;
  @ViewChild(CallPathwayRoleplayComponent) private roleplayComponent?: CallPathwayRoleplayComponent;

  private currentStepUuid: string = '';
  private currentCallUuid: string = '';
  private currentCallType: string = '';
  private currentStepState = signal<PathwayStepState | null>(null);
  private isIntroStepCompleted: boolean = false;
  private isAutoCompleted: boolean = false;
  private readonly isStepCompleted = signal<boolean>(false);
  public instanceUuid: string = '';
  private moduleAssignmentUuid: string = '';
  public callLearnUuid = signal<string>('');
  private pathwayCompletedOnLastStep: boolean = false;
  private conversationState = signal<ConversationState | null>(null);
  private isEndingCall = false;
  private isDestroyed = false;
  private readonly isVideoCurrentlyPlaying = signal(false);
  private isStepInitializationPending: boolean = false;
  private readonly isQuizResultProcessing = signal<boolean>(false);

  private hasCallStarted(): boolean {
    return (this.realtimeService?.joinedMeeting ?? false) || !!this.conversationState();
  }
  private deviceSettingsModalContext: DeviceSettingsModalContext | null = null;

  private learnWithXMicOnEnabled = false;

  constructor(
    private readonly pathwaysService: PathwaysService,
    private readonly callService: CallService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translationService: TranslationService,
    private readonly loggerService: LoggerService,
    private readonly audioCallControlService: AudioCallControlService,
    private readonly pathwaySubtitleService: PathwaySubtitleService,
    private readonly featureFlagsService: FeatureFlagsService,
  ) {
    effect(() => {
      const speech = this.playerService?.avatarSpeech?.() || '';
      this.captionText.set(speech);
    });

    effect(() => {
      this.disableConfirmActionButton.set(this.shouldDisableConfirmButton());
    });


    // Switch to quiz-result view once terminateConversation succeeds
    effect(() => {
      if (this.callStore?.getShowQuizResultView()) {
        this.quizResultTtsDone.set(false);
        this.callFlow.set(PathwayConstants.CALL_FLOW_QUIZ_RESULT);
      }
    });

    // Sync enrollmentStepUuid and stepType to URL on every step transition (skip initial @Input values)
    effect(() => {
      const uuid = this._enrollmentStepUuid();
      const stepType = this._stepType();
      if (!this._enrollmentStepUuidReady || !uuid || !stepType) return;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { enrollmentStepUuid: uuid, stepType },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

  }

  // header
  title = signal('');
  isLoading = signal(false);
  leaveStepModalOpen = signal(false);
  leaveStepModalDescription = signal('');
  learnStepHelpModalOpen = signal(false);
  readonly roleplayEndedEarly = signal(false);
  private pendingRoleplayOnComplete: (() => void) | undefined;

  protected get disableVideoButton(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return true;
    }
    return !this.callStore?.getCall();
  }

  protected get isLearnStep(): boolean {
    return this.callFlow() === PathwayConstants.CALL_FLOW_PDF ||
      this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO ||
      this.callFlow() === PathwayConstants.CALL_FLOW_SCORM;
  }

  protected get learnStepType(): 'pdf' | 'video' | 'scorm' | null {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) return 'pdf';
    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) return 'video';
    if (this.callFlow() === PathwayConstants.CALL_FLOW_SCORM) return 'scorm';
    return null;
  }

  protected handleHelpIconClick(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.videoComponent?.pauseVideo();
    } else if (this.callFlow() === PathwayConstants.CALL_FLOW_SCORM) {
      this.scormComponent?.pauseVideo();
    }
    this.learnStepHelpModalOpen.set(true);
  }

  private initQuizGuidedCall(response: IPathwayRunStepResponse): void {
    this.confirmActionLabel.set('learningPathway.SUBMIT_ANSWER');
    this.disableConfirmActionButton.set(true);

    if (!response.callUuid) {
      this.loggerService.error('Missing callUuid in QUIZ IN_PROGRESS step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.currentCallType = response.callType;
    this.enrollmentStepUuid = response.stepUuid;


    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for QUIZ IN_PROGRESS step setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );
      this.controlService.interactionCount = 0;
      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.callStore?.setShowQuizResultView(false);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');

    if (!this.realtimeService) {
      // Fallback to legacy Tavus start if realtimeService is not available
      this.startTavusCall(false);
      this.mainIsLoading.set(false);
      return;
    }

    // Mirror what startCallRequest(doGuidedValidation=true) sets up before fetching guided info
    this.realtimeService.isGuidedCall = true;
    this.realtimeService.isFirstGuidedInteraction = true;
    this.realtimeService.guidedIndex = 0;
    this.callStore?.setIsContinueButtonHidden(false);
    this.callStore?.setIsContinueButtonBlocked(true);

    // When the guided quiz call auto-completes (avatar finishes last question), show results
    this.realtimeService.onAutoComplete = () => {
      this.realtimeService!.onAutoComplete = null;
      // sendRequestToEndCall is normally triggered via control.service on manual submit;
      // in the auto-complete path it must be called explicitly here.
      this.realtimeService!.sendEndCallRequest();
      this.realtimeService!.isGuidedCall = false;
      this.handleQuizCompletionAndShowResults().catch((err) => {
        this.loggerService.error('[PATHWAY] handleQuizCompletionAndShowResults failed', err);
        this.setErrorState(err);
      });
    };

    if (!this.moduleAssignmentUuid) {
      this.setErrorState('Missing moduleAssignmentUuid for QUIZ pathway simulation init');
      return;
    }

    // For QUIZ steps, initialize the practice call and Tavus conversation via
    // initiatePathwayStepSimulation. Guided metadata (questions, hints, visual aids)
    // will still be fetched separately via fetchGuidedEchoInfo.
    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          this.moduleAssignmentUuid,
          this.currentCallUuid,
          PathwayConstants.PRACTICE_TYPE_PATHWAY,
          AppConstants.CALL_TYPE_GUIDED,
          this.enrollmentStepUuid
        )
        .subscribe({
          next: async (res: IInitiatePathwayStepSimulationResponse) => {
            try {
              if (!this.realtimeService) {
                this.setErrorState('Missing realtimeService for QUIZ pathway simulation init');
                return;
              }

              const conversationId = res.conversationId ?? '';
              const conversationUrl = res.conversationUrl ?? '';
              if (!conversationId || !conversationUrl) {
                this.setErrorState('initiatePathwayStepSimulation response missing conversation details for QUIZ step');
                return;
              }

              // Build a minimal ICallRealtime payload from the simulation response
              const realtimePayload: ICallRealtime = {
                practiceCallUuid: res.practiceCallUuid,
                conversationId,
                conversationUrl,
                callName: res.callName ?? '',
                avatarName: res.avatarName ?? this.playerService?.avatarName ?? '',
                pronounceWords: res.pronounceWords ?? [],
                resumeMessage: res.resumeMessage ?? [],
                visualAid: res.visualAid ?? undefined,
                customBackgroundUrl: res.customBackgroundUrl ?? undefined,
                hideResultsForLearners: res.hideResultsForLearners ?? null,
              };

              if (this.controlService) {
                this.controlService.isVisualAidCall = !!res.visualAid;
              }

              // Reset the corner-animation flag for this step so the guide component
              // reliably starts closed and animates once this step's own avatar speech
              // event fires, instead of inheriting a stale true value left over from
              // an earlier step's avatar-stopped-speaking event.
              if (res.visualAid) {
                this.callStore?.setIsVisualAidOpen(false);
              }

              this.callStore?.setDisplayPostCall(false);
              await this.realtimeService.initializeFromStartConversationResponse(
                realtimePayload,
                true
              );

              this.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);
              this.realtimeService.conversationInitializedByPathwayFlow = true;

              await this.realtimeService.getFetchGuidedEchoInfo();

              // Set isVisualAidCall after getFetchGuidedEchoInfo — visualAid may have come from
              // the echo endpoint rather than the simulation response
              if (this.controlService && this.realtimeService.visualAidObject) {
                this.controlService.isVisualAidCall = true;
                this.callStore?.setIsVisualAidOpen(false);
              }

              this.mainIsLoading.set(false);
            } catch (error) {
              this.setErrorState(error as Error);
            }
          },
          error: (err) => {
            this.setErrorState(err);
          },
        })
    );
  }

  private shouldDisableConfirmButton(): boolean {
    // Content not yet consumed (e.g. video unwatched, PDF unread).
    // Checked first so TTS state changes (isContinueButtonBlocked) cannot override it.
    if (!this.isStepCompleted()) {
      return true;
    }

    // For PDF learn steps on a first-time attempt, gate on welcome message completion.
    if (
      this.callFlow() === PathwayConstants.CALL_FLOW_PDF &&
      this.isFirstAttempt() &&
      !this.isWelcomeDoneForCurrentStep()
    ) {
      return true;
    }

    // Quiz results are being scored/fetched — ignore stale conversation-turn
    // flags (e.g. isContinueButtonBlocked) entirely while this is true.
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      return this.isQuizResultProcessing();
    }

    // Require a transcript before the user can submit
    const isQuizStep = untracked(() => this.stepType) === PathwayStepType.QUIZ;
    if (isQuizStep) {
      const hasTranscript =
        (this.callStore?.getSpeechTranscript().length ?? 0) !== 0 ||
        (this.callStore?.getSpeechTranscriptPartial().length ?? 0) !== 0;
      if (!hasTranscript) return true;
    }

    // Keep locked while the avatar is speaking or an answer is processing
    return this.callStore?.getIsContinueButtonBlocked() ?? false;
  }

  protected get disableMicButton(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return true;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      return true;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      // Disable mic while coach is speaking
      return this.audioCallControlService.isAgentSpeaking;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) {
      return this.audioCallControlService.isAgentSpeaking;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_SCORM) {
      return this.audioCallControlService.isAgentSpeaking;
    }

    // Existing behavior for realtime/Tavus flows
    return !!this.realtimeService?.isFirstMessage;
  }

  disableConfirmActionButton = signal(false);
  confirmActionLabel = signal('Next Step');

  // main
  mainIsLoading = signal(false);
  callFlow = signal<'guide' | 'pdf' | 'video' | 'scorm' | 'error' | 'quiz-result' | 'role-play'>(PathwayConstants.CALL_FLOW_GUIDE);
  isPreamble = signal(false);
  rolePlayCallUuid = signal<string>('');
  rolePlayCallType = signal<string>('');
  runStepResponse = signal<IPathwayRunStepResponse | null>(null);

  isFirstAttempt = signal<boolean>(true);
  quizOutcome = signal<string>(AppConstants.QUIZ_PASSED_NEXT_STEP);
  quizPassed = signal<boolean>(true);
  quizResultTtsReady = signal<boolean>(false);
  quizStepsToReview = signal<string[]>([]);
  private quizCompletionNextStepResponse: IPathwayNextStepResponse | null = null;

  videoSrc = signal('https://simulation-temporary-test.s3.amazonaws.com/MenquadfiPreCall.mp4');
  videoStartTimeMs = signal<number>(0);
  pdfViewerUrl = signal<string>(DEFAULT_PDF_URL);
  initialPdfPage = signal<number>(1);
  scormUrl = signal<string>('');
  scormInitialState = signal<IScormLearnState | null>(null);
  /** SCORM package root customer_content uuid + embedded-asset catalog (learnerContext). */
  scormPackageContentUuid = signal<string>('');
  scormChildren = signal<ICallScormChild[]>([]);
  /** Converse identifiers for the SCORM step's voice turns (processContentQuestion). */
  scormCallUuid = signal<string>('');
  scormPracticeCallUuid = signal<string>('');
  /** Caption text shown in footer (e.g. from originalWelcomeMessage or other APIs). */
  captionText = signal<string>('');
  /** True while quiz-result is playing outcome TTS (so footer shows Speaking + caption). */
  quizResultTtsPlaying = signal<boolean>(false);
  /** True once quiz-result outcome TTS has finished — used to hide avatar and captions. */
  protected quizResultTtsDone = signal<boolean>(false);
  showAvatarPhoto = signal<boolean>(false);
  avatarPhotoUrl = signal<string>('');
  isPdfDownloadEnabled = signal<boolean>(false);
  // Error state
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  // Pathway flow state
  private readonly REQUIRED_INTERACTIONS = 0;
  private interactionCheckInterval: any;
  private subscriptions: Subscription[] = [];
  private welcomeMessageDoneSub?: Subscription;
  private readonly isWelcomeDoneForCurrentStep = signal(false);
  private lastPdfProgress: IPdfProgress | null = null;
  private videoPositionMs: number = 0;
  private videoDurationMs: number = 0;
  private videoProgressInitialized = false;
  private readonly VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL = 10000; // 10s

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('learningPathway');
    this.translationService.loadScopedTranslation('call');

    this.featureFlagsService
      .isFlagEnabled(PathwayConstants.FLAG_LEARN_WITH_X_MIC_ON)
      .then((enabled) => { this.learnWithXMicOnEnabled = enabled; })
      .catch(() => { this.learnWithXMicOnEnabled = false; });

    // Prefer moduleAssignmentUuid from CallStore, fallback to route query param
    this.moduleAssignmentUuid =
      this.callStore?.getModuleAssignmentUuid() || this.route.snapshot.queryParams['moduleAssignmentUuid'] || '';

    if (this.controlService && this.callStore && this.moduleAssignmentUuid) {
      const userUuid = localStorage.getItem('user_uuid') || '';
      this.callStore.setUserUuid(userUuid);
      this.callStore.setModuleAssignmentType(PathwayConstants.PRACTICE_TYPE_PATHWAY);
      this.controlService.isRealTimeCall = true;

      const maxAttemptsParam = this.route.snapshot.queryParams['maxAttemptsAllowed'];
      this.callStore.setMaxAttemptsAllowed(
        maxAttemptsParam != null ? Number(maxAttemptsParam) : null,
      );

      this.initializeFlowFromRoute();
    }

    this.subscriptions.push(
      this.audioCallControlService.onCaptionText$.subscribe((text) =>
        this.captionText.set(text)
      )
    );

    this.subscriptions.push(
      this.audioCallControlService.onStateChange$.subscribe((state) => {
        const prevState = this.conversationState();
        this.conversationState.set(state);

        if (
          prevState === ConversationState.AGENT_SPEAKING &&
          state !== ConversationState.AGENT_SPEAKING
        ) {
          this.pathwaySubtitleService.startGracePeriod(this.captionText());
        }

        if (state === ConversationState.USER_SPEAKING) {
          this.pathwaySubtitleService.clearGracePeriod();
        }
      })
    );

    this.subscriptions.push(
      this.audioCallControlService.onError$.subscribe((error) => {
        this.setErrorState(error);
      })
    );

    if (this.realtimeService) {
      this.subscriptions.push(
        this.realtimeService.onError$.subscribe((error) => {
          this.setErrorState(error);
        })
      );
    }

    if (this.deviceSettingsService) {
      this.subscriptions.push(
        this.deviceSettingsService.displayModal$
          .pipe(
            pairwise(),
            filter(([prev, cur]) => prev === true && cur === false)
          )
          .subscribe(() => {
            this.handleDeviceSettingsModalClosed();
          })
      );
    }

    this._enrollmentStepUuidReady = true;
  }

  protected handleMicrophoneClick(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return;
    }
    if (
      this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO ||
      this.callFlow() === PathwayConstants.CALL_FLOW_PDF ||
      this.callFlow() === PathwayConstants.CALL_FLOW_SCORM
    ) {
      const isMicOn = this.callStore?.getIsMicOn() ?? false;
      if (!isMicOn) {
        if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
          this.videoComponent?.pauseVideo();
        } else if (this.callFlow() === PathwayConstants.CALL_FLOW_SCORM) {
          this.scormComponent?.pauseVideo();
        }
        this.audioCallControlService.setMicMuted(false).catch(console.error);
      } else {
        this.audioCallControlService.setMicMuted(true).catch(console.error);
      }
      return;
    }
    this.controlService?.toggleMic();
  }

  protected handleVideoClick(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return;
    }
    if (this.deviceSettingsService?.isCameraUnavailable) {
      this.openDeviceSettingsModal();
    } else {
      this.controlService?.toggleVideo();
    }
  }

  protected handleQuizResultPrimaryAction(): void {
    const outcome = this.quizOutcome();

    if (
      outcome === AppConstants.QUIZ_PASSED_LAST_STEP ||
      outcome === AppConstants.QUIZ_FAILED_LAST_STEP ||
      outcome === AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED
    ) {
      this.navigateToPathwayHome();
      return;
    }

    if (this.quizCompletionNextStepResponse?.toStepUuid && this.quizCompletionNextStepResponse.stepType) {
      this.mainIsLoading.set(true);
      this.startStep(this.quizCompletionNextStepResponse);
      return;
    }

    // Fallback: refetch and proceed with normal navigation.
    this.getNextStep();
  }

  /**
   * Footer confirm button label. When in quiz-result flow, returns outcome-specific label.
   */
  protected getConfirmActionLabel(): string {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      const outcome = this.quizOutcome();
      switch (outcome) {
        case AppConstants.QUIZ_PASSED_NEXT_STEP:
          return 'learningPathway.NEXT_STEP';
        case AppConstants.QUIZ_PASSED_LAST_STEP:
          return 'learningPathway.RETURN_TO_PATHWAY';
        case AppConstants.QUIZ_FAILED_PREVIOUS_STEP:
          return 'learningPathway.REVIEW_MATERIAL';
        case AppConstants.QUIZ_FAILED_LAST_STEP:
          return 'learningPathway.TRY_AGAIN';
        case AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED:
          return 'learningPathway.RETURN_TO_PATHWAY';
        default:
          return 'learningPathway.NEXT_STEP';
      }
    }
    return this.confirmActionLabel();
  }

  protected handleRetryRoleplay(): void {
    // Read the roleplay step UUID before callFlow change triggers component destruction.
    // enrollmentStepUuid stays at the preamble UUID in this component (setupNormalStep for
    // roleplay runs inside PathwayRoleplayService), so lastRoleplayStepUuid is the
    // only reliable source of the actual step the user just completed.
    const stepUuid =
      this.roleplayComponent?.roleplayService?.lastRoleplayStepUuid ||
      this.enrollmentStepUuid;
    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.runStepAfterPreamble(stepUuid);
  }

  protected async handleRoleplayConfirmAction(onComplete?: () => void): Promise<void> {
    if (!onComplete) {
      // Header X confirmed — end the call (fires exitStepByEnrollmentUuid when threshold not met),
      // then navigate home.
      this.mainIsLoading.set(true);
      await this.endRealtimeCall();
      // The learner may have navigated away (e.g. browser back) while the exit was in flight.
      if (!this.isDestroyed) this.navigateToPathwayHome();
      return;
    }

    // Its regular case when we need to move to next step
    const nextStep =
      this.roleplayComponent?.roleplayService?.nextStepResponse();
    if (nextStep) {
      onComplete();
      if (!nextStep.toStepUuid) {
        this.navigateToPathwayHome();
      } else {
        this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
        this.mainIsLoading.set(true);
        this.startStep(nextStep);
      }
      return;
    }

    const isComplete =
      (this.controlService?.interactionCount ?? 0) >=
      (this.controlService?.interactionRequirement ?? 4);
    this.roleplayEndedEarly.set(!isComplete);

    this.handleOnConfirmActionClick(onComplete);
  }

  protected async handleOnConfirmActionClick(onComplete?: () => void): Promise<void> {
    const existTranscript =
        (this.callStore?.getSpeechTranscript().length ?? 0) > 0 ||
        (this.callStore?.getRecognizedTranscripts().length ?? 0) > 0 ||
        (this.callStore?.getSpeechTranscriptPartial().length ?? 0) > 0;
    const isQuiz = this.stepType === PathwayStepType.QUIZ;
    const isCallComplete = this.callStore?.getIsCallComplete();
    if (this.callFlow() === PathwayConstants.CALL_FLOW_ERROR) {
      return;
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      this.handleQuizResultPrimaryAction();
      return;
    }

    if (isQuiz && existTranscript && !isCallComplete) {
      this.realtimeService?.handleClickContinue().catch((err) => {
        this.loggerService.error('handleClickContinue failed', err);
      });
      return;
    }

    if (isQuiz && !this.isPreamble()) {
      return;
    }

    const isLearnStep =
      this.callFlow() === PathwayConstants.CALL_FLOW_PDF ||
      this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO;
    if (isLearnStep) {
      await this.audioCallControlService.endCall().catch((err) => {
        this.loggerService.error('Audio call end failed', err);
      });
      const practiceCallUuid = this.callStore?.getPracticeCallUuid();
      if (practiceCallUuid) {
        this.callService.endLearnCall(practiceCallUuid).subscribe({
          error: (err: any) =>
            this.loggerService.error('endLearnCall failed', err),
        });
      }
    }
    if (this.isPreamble()) {
      if (this.isAutoCompleted) {
        // Preamble: if welcome message was spoken, skip complete API and go directly to main step after preamble
        this.runCurrentStepAfterPreamble();
      } else {
        // Manual preamble confirm path: complete preamble first, then continue flow.
        this.completeStepAndNavigateToNext();
      }
    } else if (this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
      if (this.isIntroStepCompleted) {
        // Intro: if welcome message was spoken, skip complete API and go directly to getNextStep
        if (this.isAutoCompleted) {
          this.getNextStep();
        } else {
          this.completeStepAndNavigateToNext();
        }
      }
    } else {
      // For all other steps: check if step has been completed (e.g., video ended, PDF last page reached)
      if (this.isStepCompleted()) {
        if (this.pathwayCompletedOnLastStep) {
          this.pathwayCompletedOnLastStep = false;
          this.navigateToPathwayHome();
        } else {
          this.getNextStep();
        }
      } else if (this.isRoleplayStepType) {
        this.mainIsLoading.set(true);
        await this.endRealtimeCall();
        this.completeStepAndNavigateToNext(onComplete);
      } else if (isLearnStep) {
        this.completeStepAndNavigateToNext();
      }
    }
  }

  private async handleQuizCompletionAndShowResults(): Promise<void> {
    this.mainIsLoading.set(true);
    this.disableConfirmActionButton.set(true);
    this.isQuizResultProcessing.set(true);

    // Step 1: Synchronous cleanup of Daily/Tavus call resources
    this.realtimeService?.cleanupCallResources();

    // Step 2: TERMINATE — wait for backend terminateConversation to finish
    try {
      await this.realtimeService?.terminateConversationAsync();
    } catch (error) {
      this.loggerService.error('terminateConversation failed on quiz completion', error);
    }

    // Step 3: SHOW RESULTS — render the quiz-result view and start result data polling
    this.callStore?.setIsMicOn(false);
    this.callStore?.setQuizResultReady(false);
    await this.setQuizCompletedUrlFlag(true);
    this.callStore?.setShowQuizResultView(true);
    this.mainIsLoading.set(false);

    try {
      // Step 4: WAIT FOR QUIZ DATA — blocks until getPathwayQuizResultData returns in ResultsGuidedComponent
      await this.waitForQuizResultData();

      // Step 5: FETCH OUTCOME — wait for getNextStep API to resolve
      await this.fetchQuizCompletionOutcomeAsync();
    } catch (error) {
      this.loggerService.error('[PATHWAY] Quiz results could not be loaded, redirecting to pathway home', error);
      const practiceCallUuid = this.route.snapshot.queryParams['practiceCallUuid'];
      if (practiceCallUuid) PathwayLocalStorageUtil.markQuizPending(practiceCallUuid);
      this.navigateToPathwayHome();
      return;
    }

    this.isQuizResultProcessing.set(false);
    // Step 6: PLAY TTS — outcome is now known, trigger TTS in quiz-result component
    this.audioCallControlService.skipListenCycleOnNextSpeakEnd();
    this.quizResultTtsReady.set(true);
  }

  private async restoreQuizResultsFromReload(): Promise<void> {
    if (this.controlService) {
      this.controlService.isVisualAidCall = this.route.snapshot.queryParams['visualAidCall'] === 'true';
    }
    this.isStepCompleted.set(true);
    this.mainIsLoading.set(true);
    this.isQuizResultProcessing.set(true);
    this.callStore?.setIsMicOn(false);
    this.callStore?.setQuizResultReady(false);
    this.callStore?.setShowQuizResultView(true);
    this.mainIsLoading.set(false);

    try {
      await this.waitForQuizResultData();
      await this.fetchQuizCompletionOutcomeAsync();
      this.isQuizResultProcessing.set(false);
      this.audioCallControlService.skipListenCycleOnNextSpeakEnd();
      this.quizResultTtsReady.set(true);
    } catch (error) {
      this.loggerService.error('[PATHWAY] Quiz results restore on reload failed', error);
      const practiceCallUuid = this.route.snapshot.queryParams['practiceCallUuid'];
      if (practiceCallUuid) PathwayLocalStorageUtil.markQuizPending(practiceCallUuid);
      this.navigateToPathwayHome();
    }
  }

  private waitForQuizResultData(): Promise<void> {
    if (this.callStore?.getQuizResultReady()) {
      return Promise.resolve();
    }
    return firstValueFrom(
      race(
        this.callStore!.onQuizResultDataReady$(),
        this.callStore!.onQuizResultDataError$().pipe(
          switchMap(() => throwError(() => new Error('[PATHWAY] Quiz result data error')))
        ),
        this.callStore!.onQuizResultPollingTimeout$().pipe(
          switchMap(() => throwError(() => new Error('[PATHWAY] Quiz result polling timed out')))
        )
      )
    );
  }

  private async fetchQuizCompletionOutcomeAsync(): Promise<void> {
    if (!this.enrollmentUuid) {
      this.loggerService.error('Missing enrollmentUuid for fetchQuizCompletionOutcomeAsync');
      return;
    }

    try {
      const response = await firstValueFrom(
        timer(3000).pipe(
          switchMap(() =>
            this.pathwaysService.getNextStepByEnrollmentUuid(this.enrollmentUuid)
          )
        )
      );

      this.quizCompletionNextStepResponse = response;
      this.quizStepsToReview.set(response.stepsToReview ?? []);

      // Pass/fail for TTS is driven by scoring criteria from getPathwayQuizResultData.
      const quizResultData = this.callStore?.getQuizResultData();
      const score = quizResultData?.practiceCall?.score ?? 0;
      const threshold = quizResultData?.stepCompletionRequirement?.conditionValue ?? 0;
      this.quizPassed.set(score >= threshold);

      if (
        response.status === PathwayConstants.API_STATUS_BLOCKED &&
        response.reason === PathwayConstants.REASON_ENROLLMENT_LOCKED
      ) {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED);
        return;
      }

      // CTA label and navigation are driven entirely by getNextStep response:
      // - toStepUuid has value + reason NEXT_STEP_AVAILABLE → passed, move to next step
      // - toStepUuid has value + reason COMPLETION_REQUIREMENT_FAILED → failed, move to failure target
      // - toStepUuid empty + reason PATHWAY_COMPLETED → final quiz passed
      // - toStepUuid empty + reason COMPLETION_REQUIREMENT_FAILED → final quiz failed
      // - toStepUuid empty + reason MAX_ATTEMPT_REACHED → max attempts reached
      const hasToStep = !!response.toStepUuid;
      const reason = response.reason;

      if (hasToStep && reason === 'NEXT_STEP_AVAILABLE') {
        this.quizOutcome.set(AppConstants.QUIZ_PASSED_NEXT_STEP);
        this.disableConfirmActionButton.set(false);
      } else if (hasToStep && reason === 'COMPLETION_REQUIREMENT_FAILED') {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_PREVIOUS_STEP);
      } else if (!hasToStep && reason === 'PATHWAY_COMPLETED') {
        this.quizOutcome.set(AppConstants.QUIZ_PASSED_LAST_STEP);
      } else if (!hasToStep && reason === 'COMPLETION_REQUIREMENT_FAILED') {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_LAST_STEP);
      } else if (!hasToStep && reason === 'MAX_ATTEMPT_REACHED') {
        this.quizOutcome.set(AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED);
      }
    } catch (error) {
      this.loggerService.error('fetchQuizCompletionOutcomeAsync failed', error);
      throw error;
    }
  }

  protected handleSettingsClick(): void {
    this.openDeviceSettingsModal();
  }

  private openDeviceSettingsModal(): void {
    if (!this.deviceSettingsService) return;
    this.deviceSettingsModalContext = {
      wasPausedBeforeOpen: this.callStore?.getIsPaused() ?? false,
      micOnBeforeOpen: this.callStore?.getIsMicOn() ?? false,
      videoOnBeforeOpen: this.controlService?.isVideoOn ?? false,
    };
    this.deviceSettingsService.displayDeviceSettingsModal();
  }

  private handleDeviceSettingsModalClosed(): void {
    const ctx = this.deviceSettingsModalContext;
    // Only act if this component initiated the modal.
    if (!ctx) return;
    this.deviceSettingsModalContext = null;

    // In viewResults mode there is no active call. displayDeviceSettingsModal() still sets
    // isPaused=true as a side-effect; clear it immediately so the pause modal never shows.
    if (this.route.snapshot.queryParams['viewResults'] === 'true') {
      this.callStore?.setIsPaused(false);
      return;
    }

    if (this.callStore?.getIsAudioCallMode()) {
      return;
    }

    if (ctx.wasPausedBeforeOpen) return;

    const isPaused = this.callStore?.getIsPaused() ?? false;
    if (!isPaused) return;

    if (this.controlService) {
      this.controlService.isMicOnBeforePause = ctx.micOnBeforeOpen;
      this.controlService.isVideoOnBeforePause = ctx.videoOnBeforeOpen;
    }

    if (this.controlService?.isRealTimeCall) {
      // For guided calls, confirmSettings() handles unpause with proper store context already set.
      // Calling unpauseCall() here would fire before resumeFromModalType is set, causing a double unpause.
      // For all other realtime calls (including roleplay), auto-resume so the pause modal never shows.
      if (!this.realtimeService?.isGuided()) {
        this.realtimeService?.unpauseCall();
      }
      return;
    }
    this.controlService?.unpauseCall();
  }

  protected async handleLeaveStepClick(): Promise<void> {
    // Only show modal for QUIZ step
    if (this.stepType === PathwayStepType.QUIZ && !this.isPreamble() && this.callFlow() !== PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      this.leaveStepModalDescription.set(this.translationService.translate('EXITING_QUIZ_EARLY', 'learningPathway'));
      this.leaveStepModalOpen.set(true);
    } else {
      if (this.isPreamble()) {
        this.realtimeService?.endRealtimeConversation(true, true);
      } else if (this.stepType === PathwayStepType.LEARN_WITH_X) {
        this.mainIsLoading.set(true);
        await this.exitStepEarly();
      } else if (!this.roleplayEndedEarly()) {
        // Skip endRealtimeCall if the roleplay already ended early (call + exit already handled)
        this.mainIsLoading.set(true);
        await this.endRealtimeCall();
      }
      this.executeLeaveStep();
    }
  }

  protected async handleLeaveStepConfirm(): Promise<void> {
    this.leaveStepModalOpen.set(false);
    this.mainIsLoading.set(true);
    await this.endRealtimeCall();
    this.executeLeaveStep();
  }

  protected handleReturnToPathway(): void {
    const moduleAssignmentUuid = this.moduleAssignmentUuid || this.route.snapshot.queryParams['moduleAssignmentUuid'];
    const practiceType = this.route.snapshot.queryParams['practiceType'] || PathwayConstants.PRACTICE_TYPE_PATHWAY;
    if (moduleAssignmentUuid) {
      this.router.navigate(['/learning-pathway'], {
        queryParams: { moduleAssignmentUuid, practiceType },
      });
    }
  }

  private executeLeaveStep(): void {
    // The learner may have navigated away (e.g. browser back) while the exit was in flight.
    if (this.isDestroyed) return;
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    const isQuizStep = this.stepType === PathwayStepType.QUIZ;
    const isQuizResultFlow = this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT;
    if (isQuizStep || isQuizResultFlow) {
      this.resetCallState();
    }

    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) {
      this.savePdfProgressAndLeaveStep();
    } else if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.saveVideoProgressAndLeaveStep();
    } else {
      this.navigateAfterLeaveStep();
    }
  }

  private resetCallState(): void {
    this.audioCallControlService.endCall().catch((err) => {
      this.loggerService.error('audioCallControlService endCall failed on reset', err);
    });
    if (this.controlService) {
      this.controlService.isVisualAidCall = false;
    }
    this.callStore?.setSpeechTranscript('');
    this.callStore?.setIsCallComplete(false);
    this.callStore?.setQuizResultReady(false);
    this.callStore?.setShowQuizResultView(false);
    this.callStore?.setDisplayPostCall(false);
    this.callStore?.setCurrentView('');
    this.callStore?.setCallType('');
    this.callStore?.setIsPaused(false);
    this.callStore?.setIsAvatarThinking(false);
    this.pathwaySubtitleService.reset()
    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.quizResultTtsReady.set(false);
    this.quizStepsToReview.set([]);
  }

  private async exitStepEarly(): Promise<IPathwayExitStepResponse | null> {
    if (!this.enrollmentUuid || !this.currentStepUuid) return null;
    const res = await firstValueFrom<IPathwayExitStepResponse | null>(
      this.pathwaysService
        .exitStepByEnrollmentUuid(this.enrollmentUuid, this.currentStepUuid)
        .pipe(catchError((error) => {
          this.loggerService.error(
            `[CALL] call pathway exitStepByEnrollmentUuid - error [${error}]`,
            error,
          );
          return of(null);
        }))
    );
    if (res) {
      this.loggerService.log(
        `[CALL] call pathway exitStepByEnrollmentUuid - status [${res.status}] reason [${res.reason}] message [${res.message}] stepUuid [${res.stepUuid}] instanceUuid [${res.instanceUuid}]`,
        res,
      );
    }
    return res;
  }

  private async endRealtimeCall(): Promise<void> {
    if (!this.realtimeService) return;
    this.isEndingCall = true;
    try {
      const isComplete =
        this.controlService != null &&
        this.controlService.interactionCount >=
          this.controlService.interactionRequirement;
      this.loggerService.log(
        `[CALL] call pathway - endRealtimeCall - isComplete [${isComplete}] - stepType [${this.stepType}] - joinedMeeting [${this.realtimeService.joinedMeeting}]`,
      );

      if (this.shouldExitStepOnEnd) {
        await this.exitStepEarly();
      }

      this.loggerService.log(
        `[CALL] call pathway - endRealtimeCall - calling endRealtimeConversation - stepType [${this.stepType}] - joinedMeeting [${this.realtimeService.joinedMeeting}]`,
      );
      this.realtimeService.endRealtimeConversation(true, true);
    } catch (error) {
      this.loggerService.error(
        '[CALL] tavus - Error ending realtime conversation:',
        error,
      );
    } finally {
      this.isEndingCall = false;
    }
  }

  // Leaving the page mid-call (browser back or in-app navigation) must end the step like the
  // header X does; otherwise the instance stays IN_PROGRESS and the pathway shows it as failed.
  private endCallOnLeave(): void {
    if (!this.realtimeService) return;
    // The header X (or End) is already exiting and ending this call.
    if (this.isEndingCall) return;

    const roleplayService = this.roleplayComponent?.roleplayService;
    const practiceCallUuid = roleplayService?.practiceCallUuid() || this.controlService?.practiceCallUuid;
    const isDebrief = !!roleplayService?.debriefInstanceUuid();
    // Joining also covers the reconnect after a long pause, so it is not always a new attempt.
    const isCallActive = this.realtimeService.joinedMeeting || this.realtimeService.isJoiningCall;
    const shouldExitStep = isCallActive && !isDebrief && this.shouldExitStepOnEnd;

    const leaveContext = {
      stepType: this.stepType,
      practiceCallUuid,
      joinedMeeting: this.realtimeService.joinedMeeting,
      isJoiningCall: this.realtimeService.isJoiningCall,
      isDebrief,
      interactionCount: this.controlService?.interactionCount,
    };

    if (!isCallActive) {
      this.loggerService.log('[CALL] call pathway - left page with no active call, cancelling any call still starting', leaveContext);
      this.realtimeService.endRealtimeConversation(true, true);
      return;
    }

    if (isDebrief) {
      this.loggerService.log('[CALL] call pathway - left page mid debrief, terminating debrief call', leaveContext);
      // Terminates the debrief like its exit modal does, and releases the call's mic, camera and connections.
      this.realtimeService.terminateConversationAsync().catch(() => {});
      this.realtimeService.cleanupCallResources();
      return;
    }

    this.loggerService.log(
      shouldExitStep
        ? '[CALL] call pathway - left page mid call, exiting step and ending call'
        : '[CALL] call pathway - left page mid call, ending call for scoring',
      leaveContext,
    );
    if (this.isRoleplayStepType && practiceCallUuid) {
      if (shouldExitStep) {
        // Lets the pathway page hide this attempt if it loads before the exit request finishes.
        PathwayLocalStorageUtil.markRoleplayExited(practiceCallUuid);
      } else {
        PathwayLocalStorageUtil.markRoleplayPending(practiceCallUuid);
      }
    }
    if (shouldExitStep && this.enrollmentUuid && this.currentStepUuid) {
      // Sent once without retries: a retried exit landing late would abandon the learner's next
      // attempt of this step. On success the pathway page clears the mark once the backend stops
      // listing the call; on failure the attempt is still in progress, so it should not stay hidden.
      void this.pathwaysService
        .exitStepByEnrollmentUuidKeepalive(this.enrollmentUuid, this.currentStepUuid)
        .then((res) => {
          this.loggerService.log('[CALL] call pathway - exit on leave response', { status: res?.status ?? null, practiceCallUuid });
          if (!res || res.status === 'BLOCKED') PathwayLocalStorageUtil.clearRoleplayExited(practiceCallUuid);
        });
    }
    // Unlike the header X, the end is not held back until the exit returns: the learner has
    // already left, and a late end would hit whatever call they start next.
    this.realtimeService.endRealtimeConversation(true, true);
  }

  // Early ends that leave the attempt unscored, matching the header X behaviour.
  private get shouldExitStepOnEnd(): boolean {
    return (
      (!this.interactionsMet && this.isRoleplayStepType) ||
      (!this.isPreamble() && this.stepType === PathwayStepType.QUIZ)
    );
  }

  // ngOnDestroy doesn't run when the tab closes or reloads, so a live role-play is ended with
  // requests that outlive the page; otherwise the attempt stays IN_PROGRESS on the backend.
  @HostListener('window:pagehide')
  protected onPageHide(): void {
    const roleplayService = this.roleplayComponent?.roleplayService;
    if (!this.realtimeService?.joinedMeeting || !this.isRoleplayStepType || roleplayService?.debriefInstanceUuid()) return;

    const conversationId = this.realtimeService.conversationId;
    const practiceCallUuid = this.controlService?.practiceCallUuid;
    if (!conversationId || !practiceCallUuid) return;

    this.loggerService.log(
      this.interactionsMet
        ? '[CALL] call pathway - page closing mid role-play, sending keepalive end call for scoring'
        : '[CALL] call pathway - page closing mid role-play, sending keepalive exit step and end call',
      { stepType: this.stepType, practiceCallUuid, conversationId, interactionCount: this.controlService?.interactionCount },
    );
    if (this.interactionsMet) {
      PathwayLocalStorageUtil.markRoleplayPending(practiceCallUuid);
    } else {
      PathwayLocalStorageUtil.markRoleplayExited(practiceCallUuid);
      if (this.enrollmentUuid && this.currentStepUuid) {
        void this.pathwaysService.exitStepByEnrollmentUuidKeepalive(this.enrollmentUuid, this.currentStepUuid);
      }
    }
    this.callService.endPathwayConversationOnPageClose(conversationId, practiceCallUuid);
  }

  private get interactionsMet(): boolean {
    return (
      (this.controlService?.interactionCount ?? 0) >=
      (this.controlService?.interactionRequirement ?? 4)
    );
  }

  private savePdfProgressAndLeaveStep(): void {
    this.navigateAfterLeaveStep();

    const progress = this.lastPdfProgress;
    const isOnLastPage = progress && progress.total_pages > 0 && progress.page >= progress.total_pages;
    if (progress && this.instanceUuid && !isOnLastPage) {
      const learnState: IPdfLearnState = {
        type: 'pdf',
        page: progress.page,
        total_pages: progress.total_pages,
      };
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe();
    }
  }

  private navigateAfterLeaveStep(): void {
    if (this.moduleAssignmentUuid) {
      this.navigateToPathwayHome();
    } else {
      console.error(
        'Missing moduleAssignmentUuid in route params for leave step redirect'
      );
    }
  }

  protected onPdfProgressChange(event: IPdfProgress): void {
    const isPageChange =
      this.lastPdfProgress !== null &&
      (event.page !== this.lastPdfProgress.page || event.total_pages !== this.lastPdfProgress.total_pages);
    this.lastPdfProgress = { page: event.page, total_pages: event.total_pages };
    if (!isPageChange || !this.instanceUuid) return;
    const learnState: IPdfLearnState = {
      type: 'pdf',
      page: event.page,
      total_pages: event.total_pages,
    };
    this.subscriptions.push(
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe()
    );
  }

  protected handleLeaveStepResume(): void {
    this.leaveStepModalOpen.set(false);
  }

  protected handleVideoEnded(): void {
    this.isVideoCurrentlyPlaying.set(false);
    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.markStepCompleted();
    }
  }

  protected handlePdfCompleted(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_PDF) {
      this.markStepCompleted();
    }
  }

  protected handleScormCompleted(): void {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_SCORM) {
      this.markStepCompleted();
    }
  }

  private markStepCompleted(): void {
    this.isStepCompleted.set(true);
    this.completeStepOnly();
    // For first-attempt PDF steps the shouldDisableConfirmButton() effect re-evaluates
    // the welcome-message gate reactively; skip the direct enable to avoid bypassing it.
    if (!this.pathwayCompletedOnLastStep) {
      if (!(this.callFlow() === PathwayConstants.CALL_FLOW_PDF && this.isFirstAttempt())) {
        this.disableConfirmActionButton.set(false);
      }
    }
  }

  private resetStepCompleted(): void {
    this.isStepCompleted.set(false);
  }

  private setErrorState(error: Error | string): void {
    const errorMessage = error instanceof Error ? error.message : error;
    const previousFlow = this.callFlow();
    this.errorMessage.set(errorMessage);
    this.hasError.set(true);
    this.callFlow.set(PathwayConstants.CALL_FLOW_ERROR);
    this.disableConfirmActionButton.set(true);
    this.mainIsLoading.set(false);

    this.loggerService.error('Call pathway error:', error, {
      previousFlow,
      nextFlow: PathwayConstants.CALL_FLOW_ERROR,
      stepType: this.stepType,
      enrollmentUuid: this.enrollmentUuid,
      enrollmentStepUuid: this.enrollmentStepUuid,
      callUuid: this.callStore?.getCallUuid() ?? '',
      practiceCallUuid: this.callStore?.getPracticeCallUuid() ?? '',
    });
  }

  private clearErrorState(): void {
    this.hasError.set(false);
    this.errorMessage.set('');
  }

  protected getCameraAvailable(): boolean {
    return !this.deviceSettingsService?.isCameraUnavailable;
  }

  protected shouldShowVideoControl(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) return false;
    if (this.controlService?.isVisualAidCall) return false;
    const isQuizStep = this.stepType === PathwayStepType.QUIZ;
    return (this.stepType === PathwayConstants.STEP_TYPE_INTRO && !this.isPreamble()) || isQuizStep;
  }

  protected shouldShowMicControl(): boolean {
    if (this.callFlow() === PathwayConstants.CALL_FLOW_QUIZ_RESULT) return false;
    return true;
  }

  protected onQuizResultSubtitle(text: string | null): void {
    this.captionText.set(text ?? '');
  }

  protected onQuizResultTtsPlaying(playing: boolean): void {
    this.quizResultTtsPlaying.set(playing);
    if (playing) {
      this.quizResultTtsDone.set(false);
    } else {
      this.quizResultTtsDone.set(true);
      this.showAvatarPhoto.set(false);
      this.deviceSettingsService?.stopAudioLevelDetection();
    }
  }

  protected onQuizResultAvatarThumbnail(url: string): void {
    if (url) {
      this.avatarPhotoUrl.set(url);
      this.showAvatarPhoto.set(true);
    }
  }

  protected showDeviceSettingsScreen(): boolean {
    const isDeviceSettingsView =
      this.callStore?.getCurrentView() === AppConstants.VIEW_DEVICE_SETTINGS;
    const isModalOpen = !!this.deviceSettingsService?.displayModal;
    return !!this.isStepInitializationPending && isDeviceSettingsView && !isModalOpen;
  }

  readonly subtitle = computed(() =>
    this.pathwaySubtitleService.getSubtitle(this.buildSubtitleContext())
  );

  private buildSubtitleContext(): SubtitleContext {
    return {
      callFlow: this.callFlow(),
      stepType: this.stepType,
      conversationState: this.conversationState(),
      isAvatarSpeaking: this.callStore?.getIsAvatarSpeaking() ?? false,
      isVideoPlaying: this.isVideoCurrentlyPlaying(),
      isMicOn: this.callStore?.getIsMicOn() ?? true,
      callStarted: this.hasCallStarted(),
      isAvatarThinking: this.callStore?.getIsAvatarThinking() ?? false,
      quizResultTtsPlaying: this.quizResultTtsPlaying(),
      isPreamble: this.isPreamble(),
      captionText: this.captionText(),
      hasWelcomeMessagePlayed: this.isWelcomeDoneForCurrentStep(),
    };
  }

  ngAfterViewInit(): void {
    const isViewResults = this.route.snapshot.queryParams['viewResults'] === 'true';
    if (this.deviceSettingsService && !isViewResults) {
      this.deviceSettingsService.startAudioLevelDetection();
    }

    if (this.playerService) {
      this.callStore?.setIsAvatarSpeaking(false);
    }

    if (this.currentCallUuid) {
      this.startIntroStepInteractionMonitoring();
    }
  }

  private startTavusCall(isFraming: boolean = false): void {
    if (!this.realtimeService) {
      console.error('Missing realtimeService');
      return;
    }

    if (!this.currentCallUuid) {
      console.error('Missing callUuid from API response');
      return;
    }

    if (!this.currentCallType) {
      console.error('Missing callType from API response');
      return;
    }

    const moduleAssignmentUuid = this.callStore?.getModuleAssignmentUuid();
    if (!moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid');
      return;
    }

    if (!this.controlService) {
      console.error('Missing controlService');
      return;
    }

    if (this.controlService.callUuid !== this.currentCallUuid) {
      console.warn('Call UUID mismatch, updating controlService:', {
        controlService: this.controlService.callUuid,
        apiResponse: this.currentCallUuid,
      });
    }

    console.log('Starting Tavus call with API response values:', {
      callUuid: this.currentCallUuid,
      moduleAssignmentUuid: moduleAssignmentUuid,
      callType: this.currentCallType,
      practiceType: PathwayConstants.PRACTICE_TYPE_PATHWAY,
    });

    if (!this.enrollmentStepUuid) {
      console.error('Missing enrollmentStepUuid for framing call');
      return;
    }

    try {
      if (isFraming) {
        this.realtimeService.startFramingCallRequest(this.enrollmentStepUuid).catch((error) => {
          this.setErrorState(error);
        });
      } else {
        this.callService.showStepLanguage(moduleAssignmentUuid, this.enrollmentStepUuid);
        this.realtimeService.startCallRequest(false).catch((error) => {
          this.setErrorState(error);
        });
      }
    } catch (error) {
      this.setErrorState(error as Error);
    }
  }

  private startIntroStepInteractionMonitoring(): void {
    if (this.currentCallUuid && this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
      this.isPreamble.set(false);
      this.confirmActionLabel.set('learningPathway.NEXT_STEP');
      this.isIntroStepCompleted = false;

      // Pathway calls are always realtime (Tavus calls)
      if (this.realtimeService) {
        this.disableConfirmActionButton.set(true);
        this.interactionCheckInterval = setInterval(() => {
          if (this.isPreamble()) {
            clearInterval(this.interactionCheckInterval);
            this.interactionCheckInterval = null;
            return;
          }

          const count = this.realtimeService?.avatarInteractionCount || 0;
          const hasEnoughInteractions = count >= this.REQUIRED_INTERACTIONS;

          if (this.currentCallType === AppConstants.CALL_TYPE_FRAMING) {
            this.disableConfirmActionButton.set(true);
            return;
          }

          if (hasEnoughInteractions) {
            // INTRO step is now completed (required interactions met)
            this.isIntroStepCompleted = true;
            this.disableConfirmActionButton.set(false);
            clearInterval(this.interactionCheckInterval);
            this.interactionCheckInterval = null;
          } else {
            this.disableConfirmActionButton.set(true);
          }
        }, PathwayConstants.POLLING_INTERVAL_MS);
      }
    }
  }

  private initializeFlowFromRoute(): void {
    const practiceCallUuid = this.route.snapshot.queryParams['practiceCallUuid'];
    const isViewResults   = this.route.snapshot.queryParams['viewResults']   === 'true';
    const isStartDebrief  = this.route.snapshot.queryParams['startDebrief']  === 'true';
    const isQuizCompleted = this.route.snapshot.queryParams['quizCompleted'] === 'true';

    // Reloading onto a finished step's results doesn't start the step, so show its language here.
    if (practiceCallUuid && (isViewResults || isStartDebrief || isQuizCompleted) && this.enrollmentStepUuid) {
      this.callService.showStepLanguage(this.moduleAssignmentUuid, this.enrollmentStepUuid);
    }

    if ((isViewResults || isStartDebrief) && practiceCallUuid) {
      this.title.set(this.route.snapshot.queryParams['stepTitle'] ?? '');
      this.callFlow.set('role-play');
    } else if (isQuizCompleted && practiceCallUuid) {
      this.title.set(this.route.snapshot.queryParams['stepTitle'] ?? '');
      this.restoreQuizResultsFromReload();
    } else if (this.enrollmentUuid && this.enrollmentStepUuid) {
      this.initializeStep();
    }
  }

  private initializeStep(): void {
    if (!this.enrollmentUuid || !this.enrollmentStepUuid) {
      console.error('Missing enrollmentUuid or enrollmentStepUuid for step initialization');
      return;
    }

    this.clearErrorState();
    this.title.set('');
    this.mainIsLoading.set(true);

    if (this.deviceSettingsService) {
      this.deviceSettingsService.validateDeviceAccess();

      const audioDevice = this.deviceSettingsService.getAudioDeviceLocalStorage() || '';
      const isDeviceSaved = audioDevice.length > 0;

      if (!isDeviceSaved) {
        this.isStepInitializationPending = true;
        this.subscriptions.push(
          this.deviceSettingsService.onDeviceSettingsConfirmed$.subscribe(() => {
            if (this.isStepInitializationPending) {
              this.isStepInitializationPending = false;
              this.title.set('');
              this.mainIsLoading.set(true);
              this.callStore?.setCurrentView('');

              this.proceedWithStepInitialization();
            }
          })
        );
        return;
      }
    }

    this.proceedWithStepInitialization();
  }

  private proceedWithStepInitialization(): void {
    if (!this.enrollmentUuid || !this.enrollmentStepUuid) {
      console.error('Missing enrollmentUuid or enrollmentStepUuid for step initialization');
      return;
    }

    this.avatarPhotoUrl.set('');
    this.showAvatarPhoto.set(false);
    this.subscriptions.push(
      this.pathwaysService.runStepByEnrollmentUuid(this.enrollmentUuid, this.enrollmentStepUuid).subscribe({
        next: (response: IPathwayRunStepResponse) => {
          console.log('runStepByEnrollmentUuid response:', response);

          if (response.status === PathwayConstants.API_STATUS_STARTED) {
            this.instanceUuid = response.instanceUuid ?? '';
            if (this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
              this.setupIntroStep(response);
            } else {
              if (response.isPreamble) {
                // Show preamble call
                this.setupPreambleStep(response);
              } else {
                // Normal step flow
                this.setupNormalStep(response);
              }
            }
          } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
            // Step execution is blocked
            console.error('Step execution blocked:', {
              reason: response.reason,
              message: response.message
            });

            if (response.reason === PathwayConstants.REASON_ANOTHER_STEP_ACTIVE) {
              this.getNextStep();
            } else if (response.reason === PathwayConstants.REASON_ENROLLMENT_LOCKED) {
              this.navigateToPathwayHome();
            } else {
              const errorMessage = response.message || `Step execution blocked: ${response.reason || 'Unknown reason'}`;
              this.setErrorState(errorMessage);
            }
          } else if (response.status === PathwayConstants.API_STATUS_ERROR) {
            // Error running step
            const errorMessage = response.message || `Error running step: ${response.reason || 'Unknown error'}`;
            this.setErrorState(errorMessage);
          } else {
            const errorMessage = `Unexpected runStep response status: ${response.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private setupIntroStep(response: IPathwayRunStepResponse): void {
    this.currentStepUuid = response.stepUuid || this.enrollmentStepUuid;

    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');
    this.isPreamble.set(false);
    this.confirmActionLabel.set('learningPathway.NEXT_STEP');

    if (!response.callUuid) {
      console.error('Missing callUuid in INTRO step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.enrollmentStepUuid = response.stepUuid;

    this.currentCallType = AppConstants.CALL_TYPE_FRAMING;

    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for INTRO step setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );

      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    // Set title: prefer stepName, fall back to callName
    if (response.stepName) {
      this.title.set(response.stepName);
    } else if (response.callName) {
      this.title.set(response.callName);
    }

    // Start interaction monitoring for INTRO step
    this.startIntroStepInteractionMonitoring();

    // Start Tavus call for INTRO
    this.startTavusCall(true);

    // Subscribe to welcome message completion for auto-completion
    this.isAutoCompleted = false;
    if (this.realtimeService) {
      this.subscriptions.push(
        this.realtimeService.welcomeMessageSpoken$.subscribe(() => {
          if (
            !this.isAutoCompleted &&
            this.stepType === PathwayConstants.STEP_TYPE_INTRO
          ) {
            this.isAutoCompleted = true;
            this.isIntroStepCompleted = true;
            if (this.interactionCheckInterval) {
              clearInterval(this.interactionCheckInterval);
              this.interactionCheckInterval = null;
            }
            this.completeStepOnly();
            this.disableConfirmActionButton.set(false);
          }
        })
      );
    }

    this.mainIsLoading.set(false);
  }

  private setupNormalStep(response: IPathwayRunStepResponse): void {
    this.isPdfDownloadEnabled.set(false);
    if (this.controlService) {
      this.controlService.interactionCount = 0;
      this.controlService.interactionRequirement = 4;
      this.loggerService.log(`[CALL] interaction count reset: 0/4 for new pathway step (${this.stepType})`, { stepType: this.stepType });
    }
    this.conversationState.set(null);
    this.pathwaySubtitleService.reset();
    this.callStore?.setIsAvatarThinking(false);
    this.currentStepUuid = response.stepUuid || this.enrollmentStepUuid;
    this.resetStepCompleted();
    if (!isRoleplayStepType(this.stepType)) {
      this.isStepCompleted.set(true);
    }
    this.isFirstAttempt.set(response.stepState !== PathwayStepState.COMPLETED);
    this.currentStepState.set(response.stepState ?? null);

    // Set title: prefer stepName, fall back to callName
    if (response.stepName) {
      this.title.set(response.stepName);
    } else if (response.callName) {
      this.title.set(response.callName);
    }

    this.isPreamble.set(false);
    this.confirmActionLabel.set('learningPathway.NEXT_STEP');
    this.disableConfirmActionButton.set(false);

    // Determine step flow based on step type
    // Note: runStep response doesn't include step type, so we use the component's stepType
    // QUIZ step in progress: handle as guided call
    if (this.stepType === PathwayStepType.QUIZ) {
      this.initQuizGuidedCall(response);
      return;
    }

    // this is a role-play step
    if (isRoleplayStepType(this.stepType)) {
      if (!response.callUuid) {
        this.loggerService.error('Missing callUuid in role-play step run response');
        this.mainIsLoading.set(false);
        return;
      }
      this.rolePlayCallUuid.set(response.callUuid);
      this.rolePlayCallType.set(response.callType ?? '');
      this.runStepResponse.set(response);
      this.enrollmentStepUuid = response.stepUuid;

      this.callFlow.set('role-play');
      this.mainIsLoading.set(false);
      return;
    }

    if (this.stepType === PathwayStepType.LEARN_WITH_X) {
      if (!response.callUuid) {
        this.loggerService.error('Missing callUuid in LEARN_WITH_X step run response');
        this.mainIsLoading.set(false);
        return;
      }

      this.currentCallUuid = response.callUuid;
      this.currentCallType = response.callType ?? PathwayStepType.LEARN_WITH_X;
      this.enrollmentStepUuid = response.stepUuid;

      if(this.isFirstAttempt()){
        this.isStepCompleted.set(false);
        this.disableConfirmActionButton.set(true);
      } else {
        this.disableConfirmActionButton.set(false);
      }
      this.callStore?.setIsMicOn(this.learnWithXMicOnEnabled);
      this.callStore?.setUserMicPreference(this.learnWithXMicOnEnabled);
      this.showAvatarPhoto.set(true);

      this.initiatePathwayStepSimulationForLearnWithX(response);
      return;
    }

    // Default flow for non-LEARN_WITH_X steps is guide
    if (!response.callUuid) {
      console.error('Missing callUuid in normal step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.currentCallType = response.callType;
    this.enrollmentStepUuid = response.stepUuid;


    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for normal step setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );

      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');
    this.startTavusCall(false);
    this.mainIsLoading.set(false);
  }

  protected handleDownloadPdf(): void {
    const url = this.pdfViewerUrl();
    if (!url) return;
    downloadFileFromUrl(url).catch(err => this.loggerService.error('[PDF] Download failed', err));
  }

  private initiatePathwayStepSimulationForLearnWithX(
    runStepResponse: IPathwayRunStepResponse
  ): void {
    if (
      !this.moduleAssignmentUuid ||
      !this.currentCallUuid ||
      !this.enrollmentStepUuid
    ) {
      this.loggerService.error(
        'Missing moduleAssignmentUuid, currentCallUuid or enrollmentStepUuid for initiatePathwayStepSimulation'
      );
      this.mainIsLoading.set(false);
      return;
    }

    // Reset welcome gate for the incoming step
    this.welcomeMessageDoneSub?.unsubscribe();
    this.welcomeMessageDoneSub = undefined;
    this.isWelcomeDoneForCurrentStep.set(false);
    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          this.moduleAssignmentUuid,
          this.currentCallUuid,
          PathwayConstants.PRACTICE_TYPE_PATHWAY,
          runStepResponse.callType || PathwayStepType.LEARN,
          this.enrollmentStepUuid
        )
        .subscribe({
          next: (res) => {
            this.callStore?.setPracticeCallUuid(res.practiceCallUuid);
            this.callStore?.setCallUuid(this.currentCallUuid);
            this.captionText.set(res.originalWelcomeMessage ?? '');
            this.audioCallControlService.setWelcomeMessage(
              res.pronunciationCorrectedWelcomeMessage ??
                res.originalWelcomeMessage ??
                ''
            );
            this.avatarPhotoUrl.set(res.avatarThumbnail ?? '');
            this.callStore?.setCallWords(res.callWords ?? []);
            this.callStore?.setReplaceWords(res.replaceWords ?? []);

            if (res.callScorm) {
              if (!res.callScorm.launchUrl) {
                this.setErrorState('initiatePathwayStepSimulation callScorm missing launchUrl');
                return;
              }

              this.welcomeMessageDoneSub = this.audioCallControlService.onWelcomeMessageDone$
                .pipe(take(1))
                .subscribe(() => {
                  this.isWelcomeDoneForCurrentStep.set(true);
                });

              this.isPdfDownloadEnabled.set(false);
              this.callFlow.set(PathwayConstants.CALL_FLOW_SCORM);
              this.scormUrl.set(res.callScorm.launchUrl);
              this.scormPackageContentUuid.set(res.callScorm.customerContentUuid);
              this.scormChildren.set(res.callScorm.children ?? []);
              this.scormCallUuid.set(this.currentCallUuid);
              this.scormPracticeCallUuid.set(this.callStore?.getPracticeCallUuid() ?? '');
              this.scormInitialState.set(this.getInitialScormStateFromResponse(runStepResponse));
              this.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);
              this.callStore?.setDisplayPostCall(false);
              this.mainIsLoading.set(false);
              return;
            }

            // Support both array and single-record shapes defensively
            const records = res.callLearnRecords ?? (
              res.callLearn ? [res.callLearn] : []
            );

            if (!records || records.length === 0) {
              this.setErrorState('initiatePathwayStepSimulation returned no callLearn records');
              return;
            }

            const primaryRecord = records[0];
            this.callLearnUuid.set(primaryRecord.uuid);

            if (!primaryRecord.contentLocation) {
              this.setErrorState('initiatePathwayStepSimulation primary record missing contentLocation');
              return;
            }

            // Subscribe before callFlow.set so the subscription is ready before
            // startCall() fires the welcome message TTS for both video and PDF.
            this.welcomeMessageDoneSub = this.audioCallControlService.onWelcomeMessageDone$
              .pipe(take(1))
              .subscribe(() => {
                this.isWelcomeDoneForCurrentStep.set(true);
              });

            if (primaryRecord.contentType === 'VIDEO') {
              this.isPdfDownloadEnabled.set(false);
              this.callFlow.set(PathwayConstants.CALL_FLOW_VIDEO);
              this.videoSrc.set(primaryRecord.contentLocation);
              this.videoStartTimeMs.set(this.getInitialVideoPositionFromResponse(runStepResponse));
              this.resetVideoProgress();
            } else {
              // Default to PDF when contentType is PDF or unknown
              this.isPdfDownloadEnabled.set(primaryRecord.allowDownload ?? false);
              this.callFlow.set(PathwayConstants.CALL_FLOW_PDF);
              this.pdfViewerUrl.set(primaryRecord.contentLocation);

              // PDF-specific state
              this.lastPdfProgress = null;
              this.initialPdfPage.set(
                this.getInitialPdfPageFromResponse(runStepResponse)
              );
            }
            this.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);
            this.callStore?.setDisplayPostCall(false);
            this.mainIsLoading.set(false);
          },
          error: (err) => {
            this.setErrorState(err);
          },
        })
    );
  }

  private getInitialPdfPageFromResponse(response: IPathwayRunStepResponse): number {
    console.log('getInitialPdfPageFromResponse response:', response);
    const progress = response.instanceProgress;
    if (!progress || progress.type !== 'pdf' || typeof progress.page !== 'number' || progress.page < 1) {
      return 1;
    }
    console.log('Initial PDF page:', progress.page);
    return progress.page;
  }

  private getInitialScormStateFromResponse(response: IPathwayRunStepResponse): IScormLearnState | null {
    const progress = response.instanceProgress;
    if (!progress || progress.type !== 'scorm') {
      return null;
    }
    return progress as IScormLearnState;
  }

  private completeStepOnly(): void {
    if (this.isRoleplayStepType) {
      this.navigateToPathwayHome();
      return;
    }

    if (!this.enrollmentUuid || !this.enrollmentStepUuid) {
      console.error('Missing enrollmentUuid or enrollmentStepUuid for completeStepOnly');
      return;
    }

    this.subscriptions.push(
      this.pathwaysService
        .completeStepByEnrollmentUuid(this.enrollmentUuid, this.enrollmentStepUuid)
        .subscribe({
          next: (response: IPathwayCompleteStepResponse) => {
            console.log('[PATHWAY] Step completed via completeStepOnly', response);
            const isPathwayCompleted =
              response.status === PathwayConstants.API_STATUS_COMPLETED &&
              response.reason === PathwayConstants.REASON_PATHWAY_COMPLETED;
            if (isPathwayCompleted) {
              this.pathwayCompletedOnLastStep = true;
              this.confirmActionLabel.set('learningPathway.COMPLETE_STEP');
            }
            if (!(this.callFlow() === PathwayConstants.CALL_FLOW_PDF && this.isFirstAttempt())) {
              this.disableConfirmActionButton.set(false);
            }
          },
          error: (error) => {
            console.error('Error completing step:', error);
          },
        })
    );
  }

  private completeStepAndNavigateToNext(onComplete?: () => void): void {
    // Show loading immediately (hides previous step)
    this.clearErrorState();
    if (!this.isPreamble() && !this.isRoleplayStepType) {
      this.title.set('');
    }
    this.mainIsLoading.set(true);

    // Clean up interaction monitoring
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    if (this.realtimeService) {
      try {
        this.loggerService.log(`[CALL] call pathway - completeStepAndNavigateToNext - calling endRealtimeConversation - stepType [${this.stepType}]`);
        this.realtimeService.endRealtimeConversation(true, true);
      } catch (error) {
        this.setErrorState(error as Error);
      }
    }

    if (this.isRoleplayStepType) {
      if (onComplete) {
        onComplete();
      } else {
        // Reset callFlow so the roleplay component is destroyed before getNextStep
        // recreates it — prevents stale state when the same step is returned.
        this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
        this.getNextStep();
      }
      return;
    }

    // Complete current step
    if (this.enrollmentUuid && this.enrollmentStepUuid) {
      this.subscriptions.push(
        this.pathwaysService.completeStepByEnrollmentUuid(this.enrollmentUuid, this.enrollmentStepUuid).subscribe({
          next: (response: IPathwayCompleteStepResponse) => {
            if (this.stepType === PathwayStepType.QUIZ) {
              return;
            }
            // Handle different completion statuses
            if (response.status === PathwayConstants.API_STATUS_PREAMBLE_COMPLETED) {
              // Preamble completed, now run the main step
              // We need to run the step again to get the main step instance
              if (response.completedStepUuid) {
                this.runStepAfterPreamble(response.completedStepUuid);
              } else {
                console.error('Missing completedStepUuid in PREAMBLE_COMPLETED response');
                this.mainIsLoading.set(false);
              }
            } else if (response.status === PathwayConstants.API_STATUS_COMPLETED && response.reason === PathwayConstants.REASON_PATHWAY_COMPLETED) {
              this.navigateToPathwayHome();
            } else if (response.status === PathwayConstants.API_STATUS_COMPLETED) {
              // Step completed successfully, get the next step
              // The getNextStep API will determine if there are more steps or if pathway is completed
              this.getNextStep();
            } else if (response.status === PathwayConstants.API_STATUS_REQUIREMENTS_NOT_MET) {
              // Step finished but requirements not met
              const errorMessage = response.message || `Step requirements not met: ${response.requirementFailureReason || 'Unknown reason'}`;
              this.setErrorState(errorMessage);
            } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
                if (response.reason === PathwayConstants.REASON_STEP_ALREADY_COMPLETED) {
                  this.getNextStep();
                } else {
                  // Step completion is blocked
                  const errorMessage = response.message || `Step completion blocked: ${response.reason || 'Unknown reason'}`;
                  this.setErrorState(errorMessage);
                }
            } else if (response.status === PathwayConstants.API_STATUS_ERROR) {
              // Error completing step
              const errorMessage = response.message || `Error completing step: ${response.reason || 'Unknown error'}`;
              this.setErrorState(errorMessage);
            } else {
              // Unexpected status, log and try to get next step
              const errorMessage = `Unexpected complete step response status: ${response.status}`;
              this.setErrorState(errorMessage);
            }
          },
          error: (error) => {
            this.setErrorState(error);
          }
        })
      );
    } else {
      // If no enrollmentUuid, just get next step
      this.getNextStep();
    }
  }

  private getNextStep(forceForQuizCompletion: boolean = false): void {
    if (!this.enrollmentUuid) {
      console.error('Missing enrollmentUuid for getNextStep');
      this.mainIsLoading.set(false);
      return;
    }

    this.clearErrorState();
    if (!this.isPreamble()) {
      this.title.set('');
    }
    this.mainIsLoading.set(true);

    // Clean up interaction monitoring
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    if (
      this.realtimeService &&
      (this.isPreamble() || this.stepType === PathwayConstants.STEP_TYPE_INTRO)
    ) {
      try {
        this.loggerService.log(`[CALL] call pathway - getNextStep - calling endRealtimeConversation - stepType [${this.stepType}] - isPreamble [${this.isPreamble()}]`);
        this.realtimeService.endRealtimeConversation(true, true);
      } catch (error) {
        this.setErrorState(error as Error);
      }
    }

    this.subscriptions.push(
      this.pathwaysService.getNextStepByEnrollmentUuid(this.enrollmentUuid).subscribe({
        next: (response: IPathwayNextStepResponse) => {
          console.log('getNextStepByEnrollmentUuid response:', response);

          if (
            !forceForQuizCompletion &&
            this.stepType === PathwayStepType.QUIZ &&
            this.callStore?.getIsCallComplete()
          ) {
            return;
          }

          if (response.status === PathwayConstants.API_STATUS_NEXT_AVAILABLE) {
            // Next step is available, start it
            if (response.toStepUuid && response.stepType) {
              this.startStep(response);
            } else {
              this.setErrorState('Missing toStepUuid or stepType in NEXT_AVAILABLE response');
            }
          } else if (response.status === PathwayConstants.API_STATUS_COMPLETED) {
            // Pathway is completed, redirect to learning-pathway page
            this.navigateToPathwayHome();
            if (!this.moduleAssignmentUuid) {
              this.setErrorState('Missing moduleAssignmentUuid for redirect to pathway home page');
            }
          } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
            // Next step is blocked
            this.navigateToPathwayHome();
          } else if (response.status === PathwayConstants.API_STATUS_REDIRECTED) {
            // Step was redirected
            console.log('Step redirected:', {
              fromStepUuid: response.fromStepUuid,
              toStepUuid: response.toStepUuid,
              redirectReason: response.redirectReason
            });
            if (response.toStepUuid && response.stepType) {
              this.startStep(response);
            } else {
              this.setErrorState('Missing toStepUuid or stepType in REDIRECTED response');
            }
          } else {
            const errorMessage = `Unexpected getNextStep response status: ${response.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private setupPreambleStep(response: IPathwayRunStepResponse): void {
    if (response.stepName) {
      this.title.set(response.stepName);
    } else if (response.callName) {
      this.title.set(response.callName);
    }

    this.currentStepUuid = response.stepUuid || this.enrollmentStepUuid;

    if (isRoleplayStepType(this.stepType)) {
      this.runStepResponse.set(response);
      this.callFlow.set('role-play');
      return;
    }

    // Reset call state
    this.callStore?.setIsCallComplete(false);
    this.callStore?.setDisplayPostCall(false);

    // Reset interaction counters
    if (this.controlService) {
      this.controlService.interactionCount = 0;
    }
    if (this.realtimeService) {
      this.realtimeService.avatarInteractionCount = 0;
      this.realtimeService.isFirstMessage = true;
    }

    // Transition to preamble state
    this.callFlow.set(PathwayConstants.CALL_FLOW_GUIDE);
    this.captionText.set('');
    this.audioCallControlService.setWelcomeMessage('');
    this.isPreamble.set(true);
    this.confirmActionLabel.set('learningPathway.BEGIN');
    this.disableConfirmActionButton.set(true);

    // Stop user video stream (no camera in preamble, except quiz preamble which shows camera)
    if (this.userVideoService && this.controlService?.isVideoOn && this.stepType !== PathwayStepType.QUIZ) {
      this.controlService.toggleVideo();
    }

    if (!response.callUuid) {
      console.error('Missing callUuid in preamble step run response');
      this.mainIsLoading.set(false);
      return;
    }

    this.currentCallUuid = response.callUuid;
    this.enrollmentStepUuid = response.stepUuid;

    this.currentCallType = AppConstants.CALL_TYPE_FRAMING;

    if (!this.moduleAssignmentUuid) {
      console.error('Missing moduleAssignmentUuid for preamble setup');
    } else if (this.controlService && this.callStore) {
      this.controlService.setQueryParams(
        this.moduleAssignmentUuid,
        this.currentCallUuid,
        PathwayConstants.PRACTICE_TYPE_PATHWAY,
        this.currentCallType,
        undefined
      );

      this.callStore.setCallUuid(this.currentCallUuid);
      this.callStore.setCallType(this.currentCallType);
    }

    // Start Tavus call for preamble
    this.startTavusCall(true);

    // Subscribe to welcome message completion for auto-completion
    this.isAutoCompleted = false;
    if (this.realtimeService) {
      this.subscriptions.push(
        this.realtimeService.welcomeMessageSpoken$.subscribe(() => {
          if (!this.isAutoCompleted && this.isPreamble()) {
            this.isAutoCompleted = true;
            this.completeStepOnly();
          }
        })
      );
    }

    this.mainIsLoading.set(false);
  }

  private runStepAfterPreamble(stepUuid: string): void {
    // After preamble is completed, run the step again to get the main step instance
    if (!this.enrollmentUuid) {
      this.setErrorState('Missing enrollmentUuid for running step after preamble');
      return;
    }

    this.subscriptions.push(
      this.pathwaysService.runStepByEnrollmentUuid(this.enrollmentUuid, stepUuid).subscribe({
        next: (runResponse: IPathwayRunStepResponse) => {
          if (runResponse.status === PathwayConstants.API_STATUS_STARTED) {
            this.instanceUuid = runResponse.instanceUuid ?? '';
            // This should be the main step (not preamble)
            if (runResponse.isPreamble) {
              console.warn('Unexpected: Received preamble after preamble completion');
            }
            // Setup the main step
            this.setupNormalStep(runResponse);
          } else if (runResponse.status === PathwayConstants.API_STATUS_BLOCKED) {
            const errorMessage = runResponse.message || `Step execution blocked after preamble: ${runResponse.reason || 'Unknown reason'}`;
            this.setErrorState(errorMessage);
          } else if (runResponse.status === PathwayConstants.API_STATUS_ERROR) {
            const errorMessage = runResponse.message || `Error running step after preamble: ${runResponse.reason || 'Unknown error'}`;
            this.setErrorState(errorMessage);
          } else {
            const errorMessage = `Unexpected runStep response status after preamble: ${runResponse.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private startStep(response: IPathwayNextStepResponse): void {
    // Clear quiz-result URL state so a reload on this new step does not re-enter quiz restore
    this.setQuizCompletedUrlFlag(false);

    // Reset call state
    this.clearErrorState();
    this.avatarPhotoUrl.set('');
    this.showAvatarPhoto.set(false);
    this.callStore?.setIsCallComplete(false);
    this.callStore?.setDisplayPostCall(false);
    this.callStore?.setIsMicOn(this.callStore?.getUserMicPreference());

    // Reset pdf download state for new step (will be re-enabled if the new step allows it)
    this.isPdfDownloadEnabled.set(false);

    // Flow: get API → run API → show step
    // Use enrollmentStepUuid from step object (runStep API requires enrollment step UUID)
    if (!response.toStepUuid) {
      this.setErrorState('Missing toStepUuid in get API response for step');
      return;
    }

    if (!this.enrollmentUuid) {
      this.setErrorState('Missing enrollmentUuid for running step');
      return;
    }

    this.subscriptions.push(
      this.pathwaysService.runStepByEnrollmentUuid(this.enrollmentUuid, response.toStepUuid).subscribe({
        next: (runResponse: IPathwayRunStepResponse) => {
          if (runResponse.status === PathwayConstants.API_STATUS_STARTED) {
            this.instanceUuid = runResponse.instanceUuid ?? '';
            // Determine step type from the getNextStep response or component's stepType
            this.stepType = response.stepType as string;

            if (this.stepType === PathwayConstants.STEP_TYPE_INTRO) {
              this.setupIntroStep(runResponse);
            } else {
              if (runResponse.isPreamble) {
                this.setupPreambleStep(runResponse);
              } else {
                this.setupNormalStep(runResponse);
              }
            }
          } else if (runResponse.status === PathwayConstants.API_STATUS_BLOCKED) {
            const errorMessage = runResponse.message || `Step execution blocked: ${runResponse.reason || 'Unknown reason'}`;
            this.setErrorState(errorMessage);
          } else if (runResponse.status === PathwayConstants.API_STATUS_ERROR) {
            const errorMessage = runResponse.message || `Error running step: ${runResponse.reason || 'Unknown error'}`;
            this.setErrorState(errorMessage);
          } else {
            const errorMessage = `Unexpected runStep response status: ${runResponse.status}`;
            this.setErrorState(errorMessage);
          }
        },
        error: (error) => {
          this.setErrorState(error);
        }
      })
    );
  }

  private runCurrentStepAfterPreamble(): void {
    if (!this.currentStepUuid) {
      this.setErrorState('Missing stepUuid for running step after preamble');
      return;
    }

    this.clearErrorState();
    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    if (this.realtimeService) {
      try {
        this.loggerService.log(`[CALL] call pathway - runCurrentStepAfterPreamble - calling endRealtimeConversation - stepType [${this.stepType}]`);
        this.realtimeService.endRealtimeConversation(true, true);
      } catch (error) {
        this.setErrorState(error as Error);
        return;
      }
    }

    this.mainIsLoading.set(true);
    this.runStepAfterPreamble(this.currentStepUuid);
  }

  private setQuizCompletedUrlFlag(active: boolean): Promise<boolean> {
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: active
        ? { quizCompleted: 'true', visualAidCall: this.controlService?.isVisualAidCall ? 'true' : 'false', stepTitle: this.title() }
        : { quizCompleted: null, practiceCallUuid: null, visualAidCall: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private navigateToPathwayHome(): void {
    const moduleAssignmentUuid = this.moduleAssignmentUuid || this.route.snapshot.queryParams['moduleAssignmentUuid'];
    const practiceType = this.route.snapshot.queryParams['practiceType'] || PathwayConstants.PRACTICE_TYPE_PATHWAY;

    this.resetCallState();
    if (moduleAssignmentUuid) {
      this.router.navigate(['/learning-pathway'], {
        queryParams: {
          moduleAssignmentUuid: moduleAssignmentUuid,
          practiceType: practiceType
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.endCallOnLeave();
    this.welcomeMessageDoneSub?.unsubscribe();
    this.callStore?.setUserMicPreference(true);
    this.callStore?.setIsAvatarThinking(false);

    if (this.callFlow() === PathwayConstants.CALL_FLOW_VIDEO) {
      this.videoComponent?.pauseVideo();
    } else if (this.callFlow() === PathwayConstants.CALL_FLOW_SCORM) {
      this.scormComponent?.pauseVideo();
    }

    if (this.realtimeService) {
      this.realtimeService.stopChromaKeying();
    }

    if (this.controlService?.intervalSpeechDetected) {
      clearInterval(this.controlService.intervalSpeechDetected);
    }

    if (this.interactionCheckInterval) {
      clearInterval(this.interactionCheckInterval);
      this.interactionCheckInterval = null;
    }

    this.pathwaySubtitleService.reset();

    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  // ─── Video progress tracking ──────────────────────────────────────────────

  /**
   * Called on every timeupdate tick from the video player.
   * Saves progress to the backend on two triggers:
   *  - A seek is detected (backward jump, or forward jump > 3 s)
   *  - The playhead crosses a VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL boundary (every 10 s).
   */
  protected onVideoTimeUpdate(event: { posMs: number; durationMs: number }): void {
    const { posMs, durationMs } = event;
    const prev = this.videoPositionMs;
    this.videoPositionMs = posMs;
    this.videoDurationMs = durationMs;

    // Seek detection: any backward jump or unusually large forward jump (> 2 s)
    const isSeek = posMs < prev || posMs > prev + 2000;
    if (isSeek && this.instanceUuid) {
      this.saveVideoProgress();
      return;
    }

    // Periodic checkpoint: save whenever the playhead crosses a 10 s boundary
    const prevCheckpoint = Math.floor(prev / this.VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL);
    const curCheckpoint = Math.floor(posMs / this.VIDEO_CHECKPOINT_MS_UPDATE_INTERVAL);
    if (curCheckpoint > prevCheckpoint && this.instanceUuid) {
      this.saveVideoProgress();
    }
  }

  protected onVideoPlay(): void {
    this.isVideoCurrentlyPlaying.set(true);
  }

  /**
   * Called when the video player emits a pause event.
   * Saves the current position so the learner can resume from the exact spot.
   */
  protected onVideoPause(): void {
    this.isVideoCurrentlyPlaying.set(false);
    if (this.instanceUuid && this.videoPositionMs > 0) {
      this.saveVideoProgress();
    }
  }

  /**
   * Persists the current video position to the backend via updateInstanceProgress.
   */
  private saveVideoProgress(): void {
    const learnState: IVideoLearnState = {
      type: 'video',
      position_ms: this.videoPositionMs,
      duration_ms: this.videoDurationMs,
    };
    this.subscriptions.push(
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe()
    );
  }

  /**
   * Saves the current video position and immediately navigates away.
   * Called when the learner confirms leaving the step early via the leave-early modal.
   */
  private saveVideoProgressAndLeaveStep(): void {
    this.navigateAfterLeaveStep();
    if (this.instanceUuid && this.videoPositionMs > 0) {
      const learnState: IVideoLearnState = {
        type: 'video',
        position_ms: this.videoPositionMs,
        duration_ms: this.videoDurationMs,
      };
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, learnState).subscribe();
    }
  }

  /**
   * Resets the in-memory video position state.
   * Called at the start of each new video step.
   */
  private resetVideoProgress(): void {
    this.videoPositionMs = 0;
    this.videoDurationMs = 0;
    this.videoProgressInitialized = false;
  }

  private getInitialVideoPositionFromResponse(response: IPathwayRunStepResponse): number {
    const progress = response.instanceProgress;
    if (!progress || progress.type !== 'video') {
      return 0;
    }
    const videoProgress = progress as IVideoLearnState;
    if (typeof videoProgress.position_ms !== 'number' || videoProgress.position_ms <= 0) {
      return 0;
    }
    return videoProgress.position_ms;
  }


  /**
   * Initial instance_progress for a new video step instance.
   */
  protected onVideoReady(event: any): void {
    if (this.videoProgressInitialized || !this.instanceUuid) {
      return;
    }
    this.videoProgressInitialized = true;
    if (event?.durationMs) {
      this.initializeVideoInstanceProgress(event.durationMs);
    } else {
      this.videoProgressInitialized = false; // allow retry on next loadedmetadata
    }
  }

  private initializeVideoInstanceProgress(durationMs: number): void {
    const initialState: IVideoLearnState = {
      type: 'video',
      position_ms: this.videoStartTimeMs(),
      duration_ms: durationMs,
    };
    this.subscriptions.push(
      this.pathwaysService.updateInstanceProgress(this.instanceUuid, initialState).subscribe()
    );
  }
}
